import { useState, useCallback } from 'react';
import { randomTetromino } from '../tetrominos';
import { checkCollision, rotate, STAGE_WIDTH } from '../gameHelpers';
import { Player, Stage } from '../types';

// Hook to manage player state and movement
// Hook to manage player state and upcoming tetromino preview
export const usePlayer = (): [
  Player,
  (args: { x: number; y: number; collided: boolean }) => void,
  () => void,
  (stage: Stage, dir: number) => void,
  ReturnType<typeof randomTetromino>
] => {
  const [player, setPlayer] = useState<Player>({
    pos: { x: 0, y: 0 },
    tetromino: [[0]],
    collided: false,
  });
  // Next tetromino for preview
  const [nextTetromino, setNextTetromino] = useState<ReturnType<typeof randomTetromino>>(randomTetromino());

  // Rotate the tetromino
  const rotatePlayer = useCallback(
    (stage: Stage, dir: number) => {
      const clonedPlayer: Player = JSON.parse(JSON.stringify(player));
      clonedPlayer.tetromino = rotate(clonedPlayer.tetromino, dir);

      const posX = clonedPlayer.pos.x;
      let offset = 1;
      while (checkCollision(clonedPlayer, stage, { x: 0, y: 0 })) {
        clonedPlayer.pos.x += offset;
        offset = -(offset + (offset > 0 ? 1 : -1));
        if (offset > clonedPlayer.tetromino[0].length) {
          rotate(clonedPlayer.tetromino, -dir);
          clonedPlayer.pos.x = posX;
          return;
        }
      }
      setPlayer(clonedPlayer);
    },
    [player]
  );

  // Update player position
  const updatePlayerPos = ({ x, y, collided }: { x: number; y: number; collided: boolean }) => {
    setPlayer(prev => {
      // Store the new position
      const newPos = { 
        x: prev.pos.x + x, 
        y: prev.pos.y + y 
      };
      
      return {
        ...prev,
        pos: newPos,
        collided,
      };
    });
  };

  // Reset player to start new tetromino, using nextTetromino and generate a new one
  const resetPlayer = useCallback(() => {
    const tetrominoData = nextTetromino;
    // Generate next preview
    setNextTetromino(randomTetromino());
    
    // Calculate starting position
    const startX = Math.floor(STAGE_WIDTH / 2) - Math.floor(tetrominoData.shape[0].length / 2);
    
    // Set current player piece
    setPlayer({
      pos: {
        x: startX,
        y: 0,
      },
      tetromino: tetrominoData.shape,
      collided: false,
    });
  }, [nextTetromino]);

  return [player, updatePlayerPos, resetPlayer, rotatePlayer, nextTetromino];
};